import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Alert, AuthLayout, Badge, Button, Card, CardContent, Field, Input, Select, formatMoney } from '@marketingbee/ui';
import { CA_PROVINCES, INDUSTRY_TEMPLATES, US_STATES, isIndustryTemplateId, type IndustryTemplateId } from '@marketingbee/contracts';
import { authClient } from '../../lib/auth-client';
import { errorMessage, trpc } from '../../lib/trpc';
import { setWorkspaceSlug } from '../../lib/workspace';
import { SESSION_QUERY_KEY } from '../../router/parents';
import { AuthBrand, Wordmark } from '../../shell/Logo';

const PENDING_KEY = 'sb-pending-workspace';
type Pending = { workspaceName: string; plan: 'mkt_starter' | 'mkt_growth'; region?: string; country?: 'CA' | 'US'; template?: IndustryTemplateId };

function savePending(p: Pending) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}
export function readPending(): Pending | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as Pending) : null;
  } catch {
    return null;
  }
}
export function clearPending() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

/** Industry template picker options (sign-up and workspace creation). `general` adds nothing extra. */
export const TEMPLATE_OPTIONS = INDUSTRY_TEMPLATES.map((t) => ({ value: t.id, label: t.label }));
export const TEMPLATE_HINT = 'Suggests audiences and content pillars for your first brand. You can rename or remove them any time.';

export function templateFrom(value: string | undefined | null): IndustryTemplateId {
  return isIndustryTemplateId(value) ? value : 'general';
}

export const REGION_OPTIONS = [
  ...CA_PROVINCES.map((r) => ({ value: `CA:${r.code}`, label: `${r.name} (Canada)` })),
  ...US_STATES.map((r) => ({ value: `US:${r.code}`, label: `${r.name} (US)` })),
];

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

export function PricingPage() {
  const plans = trpc.plans.useQuery();
  return (
    <div className="min-h-dvh bg-background">
      <header className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-4 sm:px-8">
        <Link to="/pricing">
          <Wordmark />
        </Link>
        <div className="flex gap-2">
          <Button asChild variant="ghost">
            <Link to="/sign-in">Sign in</Link>
          </Button>
          <Button asChild>
            <Link to="/sign-up">Start free trial</Link>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-[1100px] px-4 pb-20 pt-10 sm:px-8">
        <div className="mx-auto max-w-2xl space-y-3 text-center">
          <h1 className="text-[40px] font-semibold leading-tight tracking-[-0.03em]">Your AI marketing team</h1>
          <p className="text-[17px] text-muted-foreground">Plans, drafts and publishes your posts and emails after you approve them, then shows what worked. Prices in Canadian dollars.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {plans.data?.plans.map((p) => (
            <Card key={p.id} className={p.badge ? 'ring-2 ring-primary' : ''}>
              <CardContent className="space-y-5 py-7">
                <div className="flex items-center justify-between">
                  <h2 className="text-[20px] font-semibold">{p.name}</h2>
                  {p.badge ? <Badge variant="primary">{p.badge}</Badge> : null}
                </div>
                <p className="font-mono text-[34px] font-semibold">
                  {formatMoney(p.priceCents)} <span className="text-[15px] font-normal text-muted-foreground">CAD / month</span>
                </p>
                <ul className="space-y-2 text-[15px]">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-success" /> {h}
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full">
                  <Link to="/sign-up" search={{ plan: p.id }}>
                    Start {plans.data?.trialDays}-day free trial
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="mt-8 text-center text-[13px] text-muted-foreground">
          Prices in Canadian dollars. Taxes extra. Unlimited users on every plan.
        </p>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sign in / sign up / verify / invite
// ---------------------------------------------------------------------------

export function SignInPage() {
  const search = useSearch({ strict: false }) as { redirect?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await authClient.signIn.email({ email, password });
    setBusy(false);
    if (res.error) {
      setError(res.error.message ?? 'Sign-in failed');
      return;
    }
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    const to = search.redirect && search.redirect.startsWith('/') && !search.redirect.startsWith('//') ? search.redirect : '/dashboard';
    await navigate({ to });
  };
  return (
    <AuthLayout logo={<AuthBrand />} title="Sign in to MarketingBee" footer={<>New here? <Link className="font-semibold text-foreground underline" to="/sign-up">Start a free trial</Link> · <Link className="underline" to="/pricing">Pricing</Link></>}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <Field label="Email">
          <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Sign in
        </Button>
        <p className="text-center text-sm">
          <Link className="text-muted-foreground underline" to="/forgot-password">
            Forgot your password?
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function SignUpPage() {
  const search = useSearch({ strict: false }) as { plan?: string };
  const plans = trpc.plans.useQuery();
  const [form, setForm] = useState({ name: '', email: '', password: '', workspaceName: '', region: 'CA:ON', template: 'general', plan: search.plan === 'mkt_growth' ? 'mkt_growth' : 'mkt_starter' });
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const [country, region] = form.region.split(':') as ['CA' | 'US', string];
    savePending({ workspaceName: form.workspaceName, plan: form.plan as Pending['plan'], region, country, template: templateFrom(form.template) });
    const res = await authClient.signUp.email({ email: form.email, password: form.password, name: form.name, callbackURL: '/onboarding' });
    setBusy(false);
    if (res.error) setError(res.error.message ?? 'Sign-up failed');
    else setSent(true);
  };
  if (sent) {
    return (
      <AuthLayout logo={<AuthBrand />} title="Check your email" description={`We sent a verification link to ${form.email}. Open it to start your trial.`}>
        <p className="text-center text-sm text-muted-foreground">In local development the link is printed in the API console and written to .data/outbox.jsonl.</p>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout logo={<AuthBrand />} title="Start your 7-day free trial" description="No card needed. Choose a plan now, change it any time." footer={<>Already have an account? <Link className="font-semibold text-foreground underline" to="/sign-in">Sign in</Link></>}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <Field label="Plan">
          <Select value={form.plan} onChange={set('plan')} options={(plans.data?.plans ?? []).map((p) => ({ value: p.id, label: `${p.name} (${formatMoney(p.priceCents)}/mo)` }))} />
        </Field>
        <Field label="Your name">
          <Input required value={form.name} onChange={set('name')} />
        </Field>
        <Field label="Work email">
          <Input type="email" required value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Password" description="At least 10 characters">
          <Input type="password" minLength={10} required value={form.password} onChange={set('password')} />
        </Field>
        <Field label="Company name">
          <Input required value={form.workspaceName} onChange={set('workspaceName')} />
        </Field>
        <Field label="Province or state" description="Sets your time zone and posting times.">
          <Select value={form.region} onChange={set('region')} options={REGION_OPTIONS} />
        </Field>
        <Field label="Industry" description={TEMPLATE_HINT}>
          <Select value={form.template} onChange={set('template')} options={TEMPLATE_OPTIONS} />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const search = useSearch({ strict: false }) as { token?: string; next?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!search.token) {
      setError('This link is missing its token.');
      return;
    }
    void authClient.verifyEmail({ query: { token: search.token } }).then(async (res) => {
      if (res.error) return setError(res.error.message ?? 'The link is invalid or expired.');
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      await navigate({ to: search.next && search.next.startsWith('/') ? search.next : '/onboarding' });
    });
  }, [search.token, search.next, navigate, queryClient]);
  return (
    <AuthLayout logo={<AuthBrand />} title={error ? 'Could not verify' : 'Verifying your email…'}>
      {error ? <Alert variant="destructive">{error}</Alert> : <p className="text-center text-sm text-muted-foreground">One moment.</p>}
    </AuthLayout>
  );
}

export function InvitePage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';
  const invite = trpc.workspace.public.invitation.useQuery({ token }, { enabled: token.length >= 16 });
  const accept = trpc.workspace.public.acceptWithSignUp.useMutation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  return (
    <AuthLayout logo={<AuthBrand />} title={invite.data ? `Join ${invite.data.workspaceName}` : 'Invitation'} description={invite.data ? `Create your account for ${invite.data.email}.` : undefined}>
      {invite.error ? <Alert variant="destructive">{errorMessage(invite.error)}</Alert> : null}
      {accept.error ? <Alert variant="destructive">{errorMessage(accept.error)}</Alert> : null}
      {invite.data ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            accept.mutate(
              { token, name, password },
              {
                onSuccess: async (r) => {
                  setWorkspaceSlug(r.slug);
                  await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
                  await navigate({ to: '/dashboard' });
                },
              },
            );
          }}
        >
          <Field label="Your name">
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Password">
            <Input type="password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Button type="submit" className="w-full" loading={accept.isPending}>
            Join workspace
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/sign-in" className="underline">Sign in</Link> first.
          </p>
        </form>
      ) : null}
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Password reset (single-use link, valid 1 hour; also sent by Hive's password-reset action)
// ---------------------------------------------------------------------------

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` });
    setBusy(false);
    setSent(true);
  };
  return (
    <AuthLayout logo={<AuthBrand />} title="Reset your password" footer={<Link className="underline" to="/sign-in">Back to sign in</Link>}>
      {sent ? (
        <Alert variant="success">If an account exists for {email}, a reset link is on its way. It works once and expires in one hour.</Alert>
      ) : (
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          <Field label="Email">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Button type="submit" className="w-full" loading={busy}>
            Email me a reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string; error?: string };
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(search.error ? 'This reset link is invalid or has expired.' : null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!search.token) return setError('This reset link is missing its token.');
    setBusy(true);
    const res = await authClient.resetPassword({ newPassword: password, token: search.token });
    setBusy(false);
    if (res.error) return setError(res.error.message ?? 'This reset link is invalid or has expired.');
    await navigate({ to: '/sign-in' });
  };
  return (
    <AuthLayout logo={<AuthBrand />} title="Choose a new password">
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        <Field label="New password" description="At least 10 characters">
          <Input type="password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Set password
        </Button>
      </form>
    </AuthLayout>
  );
}
